import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, Loader2Icon } from 'lucide-react';
import { Drawer } from '../ui/Drawer';
import { realtors } from '../../data/realtors';
import type { Lead } from '../../types/portal';
interface AddLeadDrawerProps {
  open: boolean;
  existingLeads: Lead[];
  onClose: () => void;
  onCreate: (lead: Lead) => void;
}
const purposes: Lead['purpose'][] = ['Purchase', 'Refinance'];
const programs = ['Conventional', 'FHA', 'VA', 'Jumbo', 'USDA', 'FHA streamline', 'Not sure yet'];
const sources = ['Realtor referral', 'Past client referral', 'Open house sign-in', 'Website rate quote', 'Social media', 'Personal network', 'Other'];
interface FormState {
  name: string;
  phone: string;
  email: string;
  purpose: Lead['purpose'];
  amount: string;
  program: string;
  market: string;
  source: string;
  referringPartner: string;
  notes: string;
}
const emptyForm: FormState = {
  name: '',
  phone: '',
  email: '',
  purpose: 'Purchase',
  amount: '',
  program: 'Conventional',
  market: '',
  source: 'Realtor referral',
  referringPartner: '',
  notes: ''
};
function formatAmount(value: string) {
  const digits = value.replace(/[^0-9]/g, '');
  if (!digits) return '';
  return `$${Number(digits).toLocaleString('en-US')}`;
}
export function AddLeadDrawer({
  open,
  existingLeads,
  onClose,
  onCreate
}: AddLeadDrawerProps) {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const setField = (key: keyof FormState, value: string) => setForm(current => ({
    ...current,
    [key]: value
  }));
  const duplicates = useMemo(() => {
    const name = form.name.trim().toLowerCase();
    const phone = form.phone.replace(/[^0-9]/g, '');
    const email = form.email.trim().toLowerCase();
    if (name.length < 3 && phone.length < 7 && email.length < 5) return [];
    return existingLeads.filter(lead => {
      const nameMatch = name.length >= 3 && lead.name.toLowerCase().includes(name);
      const phoneMatch = phone.length >= 7 && lead.phone.replace(/[^0-9]/g, '') === phone;
      const emailMatch = email.length >= 5 && lead.email.toLowerCase() === email;
      return nameMatch || phoneMatch || emailMatch;
    });
  }, [existingLeads, form.email, form.name, form.phone]);
  const close = () => {
    setForm(emptyForm);
    setErrors({});
    setSaving(false);
    onClose();
  };
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (form.name.trim().length < 2) nextErrors.name = 'Enter the borrower’s name.';
    if (form.phone.trim().length === 0 && form.email.trim().length === 0) {
      nextErrors.phone = 'Add a phone number or an email so you can follow up.';
    }
    if (form.amount.replace(/[^0-9]/g, '').length === 0) {
      nextErrors.amount = 'Enter an estimated loan amount.';
    }
    if (form.source === 'Realtor referral' && !form.referringPartner) {
      nextErrors.referringPartner = 'Choose the referring partner.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSaving(true);
    window.setTimeout(() => {
      const partner = realtors.find(item => item.id === form.referringPartner);
      const lead: Lead = {
        id: `L-${40220 + existingLeads.length}`,
        name: form.name.trim(),
        purpose: form.purpose,
        amount: formatAmount(form.amount),
        program: form.program,
        market: form.market.trim() || 'Not specified',
        source: form.source === 'Realtor referral' && partner ? `Realtor referral — ${partner.name}` : form.source,
        assignedLabel: 'Added by you just now',
        lastContactLabel: null,
        status: 'new',
        phone: form.phone.trim() || '—',
        email: form.email.trim() || '—',
        urgent: true
      };
      onCreate(lead);
      close();
    }, 800);
  };
  return <Drawer open={open} onClose={close} eyebrow="New consumer lead" title="Add a lead" subtitle="Capture a borrower you sourced yourself. They’ll be assigned to you and added to today’s Perfect Day." data-id="element-255" data-mp="32g34ja9fstxf">
      <form className="space-y-5" onSubmit={handleSubmit} noValidate data-id="element-256" data-mp="36j871ctiovm2">
        <Field id="lead-name" label="Borrower name" required value={form.name} onChange={value => setField('name', value)} error={errors.name} placeholder="First and last name" data-id="element-257" data-mp="36k14fn6l68rj" />

        {duplicates.length > 0 ? <div className="rounded-xl border border-warn-200 bg-warn-50 p-4" data-id="element-258" data-mp="2skrp0pq7fiom">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-warn-700" data-id="element-259" data-mp="2nrp2h1juh9hx">
              <AlertTriangleIcon className="h-4 w-4" aria-hidden="true" data-id="element-260" data-mp="202487mi8qptc" />
              Possible duplicate
            </p>
            <ul className="mt-2 space-y-1.5" data-id="element-261" data-mp="1v3ji4fwpdmim">
              {duplicates.map(lead => <li key={lead.id} className="text-xs text-warn-700" data-id="element-262" data-mp="al8r614yus6d">
                  <span className="font-semibold" data-id="element-263" data-mp="fdab898dln6y">{lead.name}</span> · {lead.id}{' '}
                  · {lead.purpose} {lead.amount} · {lead.assignedLabel}
                </li>)}
            </ul>
            <p className="mt-2 text-xs text-warn-700/90" data-id="element-264" data-mp="6jnjcu7ah431">
              Log activity on the existing record instead of creating a second
              one where possible.
            </p>
          </div> : null}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-265" data-mp="yabiy1tbe1el">
          <Field id="lead-phone" label="Mobile phone" type="tel" value={form.phone} onChange={value => setField('phone', value)} error={errors.phone} placeholder="(303) 555-0100" data-id="element-266" data-mp="kcd30rzchwwz" />
          <Field id="lead-email" label="Email" type="email" value={form.email} onChange={value => setField('email', value)} placeholder="name@example.com" data-id="element-267" data-mp="3a15naqjxxqy6" />
        </div>

        <fieldset data-id="element-268" data-mp="2lyqv0xbpanhi">
          <legend className="text-sm font-semibold text-navy-800" data-id="element-269" data-mp="2ly90c54oshbp">
            Loan purpose
          </legend>
          <div className="mt-2 flex gap-2" data-id="element-270" data-mp="2h4eaejyhn646">
            {purposes.map(purpose => {
            const active = form.purpose === purpose;
            return <button key={purpose} type="button" aria-pressed={active} onClick={() => setField('purpose', purpose)} className={`inline-flex h-11 flex-1 items-center justify-center rounded-lg border text-sm font-semibold ${active ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-navy-200 text-navy-700 hover:bg-navy-50'}`} data-id="element-271" data-mp="279cory3f8m6c">
                  {purpose}
                </button>;
          })}
          </div>
        </fieldset>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-272" data-mp="4016nhgt30l3">
          <Field id="lead-amount" label="Estimated loan amount" required value={form.amount} onChange={value => setField('amount', value)} onBlur={() => setField('amount', formatAmount(form.amount))} error={errors.amount} placeholder="$450,000" data-id="element-273" data-mp="3ygchsyeljnr" />
          <SelectField id="lead-program" label="Loan program" value={form.program} onChange={value => setField('program', value)} options={programs} data-id="element-274" data-mp="3h6189m6fxefn" />
        </div>

        <Field id="lead-market" label="Market or city" value={form.market} onChange={value => setField('market', value)} placeholder="Denver, CO" data-id="element-275" data-mp="3q8snzy6cmwd3" />

        <SelectField id="lead-source" label="Lead source" value={form.source} onChange={value => setField('source', value)} options={sources} data-id="element-276" data-mp="15f71kqscdcsw" />

        {form.source === 'Realtor referral' ? <div data-id="element-277" data-mp="218vjuzzailt9">
            <label htmlFor="lead-partner" className="block text-sm font-semibold text-navy-800" data-id="element-278" data-mp="219ez28xk2w3l">
              Referring partner
            </label>
            <select id="lead-partner" value={form.referringPartner} onChange={event => setField('referringPartner', event.target.value)} aria-invalid={Boolean(errors.referringPartner)} className={`mt-1.5 h-11 w-full rounded-lg border bg-white px-3 text-sm text-navy-900 focus:outline-none focus:ring-2 ${errors.referringPartner ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-200' : 'border-navy-200 focus:border-brand-500 focus:ring-brand-200'}`} data-id="element-279" data-mp="2a88rro7rn2bv">
              <option value="" data-id="element-280" data-mp="2wjjwqpbb77v5">Select a partner</option>
              {realtors.filter(realtor => realtor.access === 'open').map(realtor => <option key={realtor.id} value={realtor.id} data-id="element-281" data-mp="2mrdv0nv99c2r">
                    {realtor.name} · {realtor.company}
                  </option>)}
            </select>
            {errors.referringPartner ? <p className="mt-1.5 text-xs font-medium text-danger-600" data-id="element-282" data-mp="2dtbxiiaqd1k8">
                {errors.referringPartner}
              </p> : null}
          </div> : null}

        <div data-id="element-283" data-mp="23uw7kinfvycb">
          <label htmlFor="lead-notes" className="block text-sm font-semibold text-navy-800" data-id="element-284" data-mp="23v4l8crdz7ok">
            Notes
          </label>
          <textarea id="lead-notes" rows={3} value={form.notes} onChange={event => setField('notes', event.target.value)} placeholder="Timeline, credit situation, property details…" className="mt-1.5 w-full rounded-lg border border-navy-200 p-3 text-sm text-navy-900 placeholder:text-navy-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200" data-id="element-285" data-mp="21y9o8et2fki" />
        </div>

        <p className="rounded-lg bg-teal-50 px-3 py-2 text-xs font-medium text-teal-700 ring-1 ring-teal-200" data-id="element-286" data-mp="1jds0gnfbdrxd">
          New leads are assigned to you and appear in Today’s Priorities until
          you log your first contact.
        </p>

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end" data-id="element-287" data-mp="2b4y2xaiic1pt">
          <button type="button" onClick={close} className="inline-flex h-11 items-center justify-center rounded-lg border border-navy-200 px-4 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-288" data-mp="1x56ios1edp3d">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-70" data-id="element-289" data-mp="qq5vbaqfuf8e">
            {saving ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-290" data-mp="s7l0cdb8miwr" /> : null}
            {saving ? 'Saving…' : 'Create lead'}
          </button>
        </div>
      </form>
    </Drawer>;
}
function Field({
  id,
  label,
  value,
  onChange,
  onBlur,
  type = 'text',
  placeholder,
  required,
  error
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
}) {
  return <div data-id="element-291" data-mp="4gp3h1rxcxin">
      <label htmlFor={id} className="block text-sm font-semibold text-navy-800" data-id="element-292" data-mp="4fxjypblqq8q">
        {label}
        {required ? <span className="ml-1 text-danger-600" aria-hidden="true" data-id="element-293" data-mp="3cv7i5mcdfbb8">
            *
          </span> : null}
      </label>
      <input id={id} type={type} value={value} onChange={event => onChange(event.target.value)} onBlur={onBlur} placeholder={placeholder} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} className={`mt-1.5 h-11 w-full rounded-lg border px-3 text-sm text-navy-900 placeholder:text-navy-400 focus:outline-none focus:ring-2 ${error ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-200' : 'border-navy-200 focus:border-brand-500 focus:ring-brand-200'}`} data-id="element-294" data-mp="1bvcczq0xibiy" />
      {error ? <p id={`${id}-error`} className="mt-1.5 text-xs font-medium text-danger-600" data-id="element-295" data-mp="12sta55x57ywp">
          {error}
        </p> : null}
    </div>;
}
function SelectField({
  id,
  label,
  value,
  onChange,
  options
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
}) {
  return <div data-id="element-296" data-mp="6o1akyln2a8p">
      <label htmlFor={id} className="block text-sm font-semibold text-navy-800" data-id="element-297" data-mp="6mq5mvggiwg0">
        {label}
      </label>
      <select id={id} value={value} onChange={event => onChange(event.target.value)} className="mt-1.5 h-11 w-full rounded-lg border border-navy-200 bg-white px-3 text-sm text-navy-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200" data-id="element-298" data-mp="2nhbvqlf2ykg">
        {options.map(option => <option key={option} value={option} data-id="element-299" data-mp="3k0bhkean6c60">
            {option}
          </option>)}
      </select>
    </div>;
}